import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LifeBuoy, Search } from 'lucide-react';
import api from '../../services/api';
import { sectionsFor, pagesFor, pathFor, aboutOf } from '../../nucleus/map';
import { knownRole } from '../../nucleus/access';
import { Glyph } from './glyphs';
import { setFind, takeSearchAsk } from '../../nucleus/find';
import { findConcepts, helpPath } from '../../nucleus/concepts';
import { pluginStateWord } from '../../pages/plugins/data/words';

// SEARCH EVERYTHING — the centre of Home's header, the only search across
// everything. One field finds across the space you stand in (the pages of
// the map, your nodes, apps, people, groups, realms and plugins) and, as you
// type, narrows the page you are on too (nucleus/find.js). Results drop below it, grouped; arrows
// move, Enter opens (on its own page, its inspector open), Esc closes.
// ⌘K (Ctrl+K) from any page comes Home and focuses it (Layout asks, nucleus/find.js).

const PER = 5;
const HELP_WORDS = ['help', 'docs', 'documentation', 'guide', 'how it works', 'concepts', 'faq', 'support'];
const arr = (r, key) => (Array.isArray(r) ? r : r?.[key] || []);
const uniq = (list) => [...new Map(list.filter((x) => x?.id != null).map((x) => [String(x.id), x])).values()];
const has = (s, q) => String(s || '').toLowerCase().includes(q);

// A PLUGIN, FOUND: by its name, what it says, or one of its tools ("send email"
// finds Mailroom, and opens that tool). One set up here opens on My plugins
// with its state beside it (Setting up, Ready, Off); one not set up, in the store.
export function pluginHit(p, term, at) {
    const tool = (p.tools || []).find((t) => t?.tool_id && has(t.name, term));
    if (!has(p.display_name || p.name, term) && !has(p.description, term) && !tool) return null;
    const name = p.display_name || p.name || 'A plugin';
    const installed = !!p.lifecycle?.installed;
    const byTool = tool && !has(name, term);
    const first = String(p.description || '').split(/(?<=[.!?])\s/)[0] || null;
    return {
        key: `plugin-${p.id}`,
        title: byTool ? `${name} · ${tool.name}` : name,
        sub: [pluginStateWord(installed ? p.lifecycle.state : 'not_installed'), byTool ? 'A tool' : first].filter(Boolean).join(' · '),
        glyph: 'plugins',
        to: installed
            ? `${at('plugins', null, p.id)}${byTool ? `&tool=${encodeURIComponent(tool.tool_id)}` : ''}`
            : at('plugins', 'store', p.id),
    };
}

// the lists that cost one read per space: read once, filtered as you type
function useLists(groupId) {
    const [lists, setLists] = useState(null);
    const load = useCallback(() => {
        if (lists) return;
        Promise.all([
            api.request(groupId ? `/groups/${groupId}/apps` : '/apps').catch(() => null),
            (groupId ? api.getGroupPrivacyRealms(groupId) : api.getPrivacyRealms()).catch(() => null),
            groupId ? Promise.resolve(null) : api.getGroups().catch(() => null),
            api.getPlugins(groupId ? { group_id: groupId } : {}).catch(() => null),
        ]).then(([apps, realms, groups, plugins]) => setLists({
            apps: uniq(Array.isArray(apps) ? apps : [...arr(apps, 'apps'), ...arr(apps, 'available_apps')]),
            realms: uniq(Array.isArray(realms) ? realms : [...arr(realms, 'realms'), ...arr(realms, 'privacy_realms')]).filter((r) => !r.is_null_realm),
            groups: arr(groups, 'groups'),
            plugins: arr(plugins, 'plugins'),
        }));
    }, [groupId, lists]);
    useEffect(() => { setLists(null); }, [groupId]);
    return [lists, load];
}

const HELP_ROOM = 76; // the Help button beside the field, and its gap

export default function GlobalSearch({ groupId = null, admin: adminGiven, onHelp }) {
    // a member finds no admin's pages: the caller may say, else what my-access answered
    const admin = adminGiven ?? (!groupId || knownRole(groupId) === 'admin');
    const hasHelp = !!onHelp;
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [live, setLive] = useState({ nodes: [], people: [] });
    const [cursor, setCursor] = useState(0);
    const [lists, loadLists] = useLists(groupId);
    const input = useRef(null);
    const box = useRef(null);
    const [room, setRoom] = useState(null);

    // CENTRED, BUT NEVER UNDER THE PAGE'S VERBS: the widest the field can be
    // while it stays centred on the desk and clear of the corner's blocks
    useEffect(() => {
        const desk = box.current?.parentElement;
        if (!desk) return undefined;
        const measure = () => {
            const d = desk.getBoundingClientRect();
            const corner = document.querySelector('.ofself-corner--tr.ofself-corner-array');
            const c = corner?.getBoundingClientRect();
            // Help stands just right of the field: leave it room
            const clearRight = (c && c.width > 0 ? c.left - 16 : d.right - 16) - (hasHelp ? HELP_ROOM : 0);
            const centre = d.left + d.width / 2;
            const centred = Math.min(460, 2 * (clearRight - centre), d.width - 48);
            // centred while it can be a decent width; else it takes the header's
            // left and runs up to the verbs
            const next = centred >= 260
                ? { width: Math.round(centred), left: '50%', shift: '-50%' }
                : { width: Math.round(Math.max(160, Math.min(460, clearRight - d.left - 24))), left: '24px', shift: '0' };
            // only a real change re-renders (a new object every time would loop)
            setRoom((cur) => (cur && cur.width === next.width && cur.left === next.left ? cur : next));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(desk);
        const corner = document.querySelector('.ofself-corner--tr.ofself-corner-array');
        if (corner) ro.observe(corner);
        const mo = new MutationObserver(measure);
        if (corner) mo.observe(corner, { childList: true, subtree: true });
        return () => { ro.disconnect(); mo.disconnect(); };
    }, [hasHelp]);
    const term = q.trim().toLowerCase();
    // the same text narrows the page you are on (nucleus/find.js)
    useEffect(() => { setFind(q.trim()); }, [q]);
    // a new page starts unnarrowed
    const { pathname } = useLocation();
    useEffect(() => { setQ(''); setOpen(false); }, [pathname]);

    // arriving from ⌘K on another page: straight into the field
    useEffect(() => {
        if (takeSearchAsk()) { input.current?.focus(); setOpen(true); loadLists(); }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ⌘K / Ctrl+K here
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                input.current?.focus();
                input.current?.select();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // close on a press outside
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // the reads that need the term: nodes and people, debounced
    useEffect(() => {
        if (term.length < 2) { setLive({ nodes: [], people: [] }); return undefined; }
        let alive = true;
        const t = setTimeout(() => {
            Promise.all([
                (groupId
                    ? api.getGroupNodes(groupId, { search: term, page: '1', per_page: String(PER) })
                    : api.request(`/nodes?${new URLSearchParams({ search: term, limit: String(PER) })}`)).catch(() => null),
                groupId ? Promise.resolve([]) : api.searchUsers(term, PER).catch(() => []),
            ]).then(([nodes, people]) => {
                if (alive) setLive({ nodes: arr(nodes, 'nodes').slice(0, PER), people: (people || []).slice(0, PER) });
            });
        }, 180);
        return () => { alive = false; clearTimeout(t); };
    }, [term, groupId]);

    const groups = useMemo(() => {
        if (!term) return [];
        const at = (section, page, open) => `${pathFor(section, page, groupId)}${open ? `?open=${encodeURIComponent(open)}` : ''}`;
        const pages = [];
        for (const sec of sectionsFor(groupId)) {
            for (const p of pagesFor(sec, groupId, { admin })) {
                const word = p.label === sec.label ? sec.label : `${sec.label} · ${p.label}`;
                if (has(word, term)) pages.push({ key: `page-${sec.id}-${p.id}`, title: word, sub: aboutOf(p, groupId) || undefined, glyph: sec.glyph, to: at(sec.id, p.id) });
            }
        }
        // the Help page is no section, so it is found by name here
        if (term.length >= 3 && HELP_WORDS.some((w) => w.startsWith(term) || term.startsWith(w))) {
            pages.unshift({ key: 'page-help', title: 'Help', sub: 'Every concept, one at a time', glyph: 'help', to: helpPath(groupId) });
        }
        const out = [
            { id: 'pages', word: 'Pages', items: pages.slice(0, PER) },
            // the concepts, as the Help page explains them: a term lands on its card, lit
            { id: 'help', word: 'Help', items: findConcepts(term).slice(0, PER)
                .map((c) => ({ key: `help-${c.id}`, title: c.word, sub: c.of ? `${c.of} · ${c.line}` : c.line, glyph: 'help', to: `${helpPath(groupId)}#${c.id}` })) },
            { id: 'nodes', word: 'Nodes', items: live.nodes.map((n) => ({ key: `node-${n.id}`, title: n.title || 'Untitled', sub: n.schema_name || n.schema?.name, glyph: 'graph', to: at('graph', null, n.id) })) },
            { id: 'apps', word: 'Apps', items: (lists?.apps || []).filter((a) => has(a.name, term) || has(a.description, term)).slice(0, PER)
                .map((a) => ({ key: `app-${a.id}`, title: a.name, sub: a.description, glyph: 'apps', to: at('apps', null, a.id) })) },
            { id: 'people', word: 'People', items: live.people.map((u) => ({ key: `person-${u.id || u.user_id}`, title: u.username || u.name, sub: u.display_name, glyph: 'people', to: at('people', null, u.id || u.user_id) })) },
            { id: 'groups', word: 'Groups', items: (lists?.groups || []).filter((g) => has(g.name, term)).slice(0, PER)
                .map((g) => ({ key: `group-${g.id}`, title: g.name, sub: g.description, glyph: 'groups', to: at('groups', null, g.id) })) },
            { id: 'realms', word: 'Realms', items: (lists?.realms || []).filter((r) => has(r.name, term) || has(r.description, term)).slice(0, PER)
                .map((r) => ({ key: `realm-${r.id}`, title: r.name, sub: r.description, glyph: 'realms', to: at('realms', null, r.id) })) },
            { id: 'plugins', word: 'Plugins', items: (lists?.plugins || []).map((p) => pluginHit(p, term, at)).filter(Boolean).slice(0, PER) },
        ];
        return out.filter((g) => g.items.length);
    }, [term, live, lists, groupId, admin]);

    const flat = groups.flatMap((g) => g.items);
    useEffect(() => { setCursor(0); }, [term]);

    const go = (item) => {
        if (!item) return;
        setOpen(false);
        setQ('');
        input.current?.blur();
        navigate(item.to);
    };
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
        else if (e.key === 'Enter') { e.preventDefault(); go(flat[cursor]); }
        else if (e.key === 'Escape') { setOpen(false); input.current?.blur(); }
    };

    let i = -1;
    return (
        <div className="nx-search" ref={box} data-open={open && term ? true : undefined} style={room ? { width: room.width, left: room.left, transform: `translateX(${room.shift})` } : undefined}>
            <label className="nx-search__field">
                <Search size={14} strokeWidth={1.7} aria-hidden="true" />
                <input
                    ref={input}
                    type="search"
                    value={q}
                    placeholder="Search everything"
                    aria-label="Search everything"
                    role="combobox"
                    aria-expanded={!!(open && term)}
                    aria-controls="nx-search-results"
                    onFocus={() => { setOpen(true); loadLists(); }}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onKeyDown={onKeyDown}
                />
                <kbd aria-hidden="true">⌘K</kbd>
            </label>
            {onHelp && (
                <button type="button" className="nx-search__help" onClick={onHelp}>
                    <LifeBuoy size={14} strokeWidth={1.6} aria-hidden="true" /> Help
                </button>
            )}
            {open && term && (
                <div className="nx-search__results ofself-float-plate" id="nx-search-results" role="listbox">
                    {groups.length === 0 && <p className="nx-search__quiet">{term.length < 2 ? 'Keep typing…' : `Nothing here matches “${q.trim()}”.`}</p>}
                    {groups.map((g) => (
                        <div key={g.id} className="nx-search__group" role="group" aria-label={g.word}>
                            <span className="nx-search__group-word">{g.word}</span>
                            {g.items.map((item) => {
                                i += 1;
                                const n = i;
                                return (
                                    <button key={item.key} type="button" role="option" aria-selected={n === cursor}
                                        className={`nx-search__item${n === cursor ? ' is-on' : ''}`}
                                        onMouseEnter={() => setCursor(n)} onClick={() => go(item)}>
                                        <Glyph name={item.glyph} size={14} strokeWidth={1.6} />
                                        <span className="nx-search__title">{item.title}</span>
                                        {item.sub && <span className="nx-search__sub">{item.sub}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
